import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell
} from 'recharts';

const GraficoContasPorMes = ({ dados = [] }) => {
  const cores = [
    'var(--chart-1, #4f8cff)',
    'var(--chart-3, #34d399)',
    'var(--chart-4, #f59e0b)',
    'var(--chart-5, #8b5cf6)',
  ];

  const formatarValorCompacto = (valor = 0) => {
    const absoluto = Math.abs(valor);
    if (absoluto >= 1000000) return `R$ ${(valor / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`;
    if (absoluto >= 1000) return `R$ ${(valor / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}k`;
    return `R$ ${valor.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
  };

  const dadosComDelta = useMemo(() => {
    return dados.map((item, index) => {
      const anterior = index > 0 ? dados[index - 1] : null;
      const valorAnterior = anterior?.valor ?? null;
      const deltaAbsoluto = valorAnterior !== null ? item.valor - valorAnterior : null;
      const deltaPercentual = valorAnterior && valorAnterior !== 0
        ? ((item.valor - valorAnterior) / valorAnterior) * 100
        : null;

      return {
        ...item,
        deltaAbsoluto,
        deltaPercentual,
      };
    });
  }, [dados]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const hasDelta = typeof data.deltaAbsoluto === 'number';
      const deltaPositivo = (data.deltaAbsoluto || 0) >= 0;
      
      return (
        <div className="bg-[color:var(--surface)] text-[color:var(--text-primary)] p-3 rounded-lg shadow-lg border border-[color:var(--border)]">
          <div className="font-semibold">{label}</div>
          <div className="text-[color:var(--accent)] font-medium">
            R$ {payload[0].value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-[color:var(--text-muted)]">
            {data.contas} conta{data.contas !== 1 ? 's' : ''}
          </div>
          {hasDelta && (
            <div className={`text-xs mt-1 border-t border-[color:var(--border)] pt-1 ${deltaPositivo ? 'text-[color:var(--status-good,var(--text-primary))]' : 'text-[color:var(--status-bad,var(--text-primary))]'}`}>
              {deltaPositivo ? '+' : '-'}
              R$ {Math.abs(data.deltaAbsoluto).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              {typeof data.deltaPercentual === 'number' && (
                <span className="ml-1">
                  ({deltaPositivo ? '+' : '-'}
                  {Math.abs(data.deltaPercentual).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)
                </span>
              )} vs mês anterior
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-lg p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)] mb-1">Contas a Receber por Mês</h3>
      <p className="text-xs sm:text-sm text-[color:var(--text-muted)] mb-3 sm:mb-4">
        Distribuição de contas a receber por período
      </p>
      
      <div className="h-44 sm:h-52 md:h-64">
        {dadosComDelta.length === 0 ? (
          <div className="h-full flex items-center justify-center border border-dashed border-[color:var(--border)] rounded-lg bg-[color:var(--surface-muted)]">
            <p className="text-sm text-[color:var(--text-muted)]">Sem dados para o período selecionado.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dadosComDelta} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis 
                dataKey="mes" 
                tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatarValorCompacto}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
              <Bar dataKey="valor" name="Valor do mês" radius={[4, 4, 0, 0]} activeBar={{ stroke: 'var(--accent)', strokeWidth: 1 }}>
                {dadosComDelta.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={cores[index % cores.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      
      <div className="mt-3 sm:mt-4">
        <p className="text-[11px] sm:text-xs text-[color:var(--text-muted)] mb-0.5">
          Total do período ({dadosComDelta.length} {dadosComDelta.length === 1 ? 'mês' : 'meses'})
        </p>
        <p className="text-sm sm:text-base font-semibold text-[color:var(--text-primary)]">
          R$ {dados.reduce((total, item) => total + item.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </p>
      </div>

      <ul className="sr-only" aria-label="Resumo tabular do gráfico de contas a receber por mês">
        {dadosComDelta.map((item) => (
          <li key={`sr-${item.mes}`}>
            {item.mes}: R$ {Number(item.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}, {item.contas} conta{item.contas !== 1 ? 's' : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GraficoContasPorMes;
